import React, { useState, Component } from 'react';
import { Users, Video, ArrowRight, AlertCircle, RefreshCw, Check, Mail, Send } from 'lucide-react';
import { Button } from './components/Button';
import { OptionCard } from './components/OptionCard';
import { Icon1 } from './components/Icon1';
import { Input } from './components/Input';
export function App() {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [showError, setShowError] = useState(false);
  const [step1Completed, setStep1Completed] = useState(false);
  const [step2Completed, setStep2Completed] = useState(false);
  const [step3Completed, setStep3Completed] = useState(false);
  const [userEmail, setUserEmail] = useState('mail@gmail.com');
  // Step 4 form state
  const [trafficSources, setTrafficSources] = useState<string[]>(['Own socials (twitter, FB, Tiktok, Insta)']);
  const [waysToPromote, setWaysToPromote] = useState<string[]>(['Way #2']);
  const [hasWebsite, setHasWebsite] = useState<'yes' | 'no' | null>('no');
  const [trafficVolume, setTrafficVolume] = useState<string>('5,000 - 50,000');
  const [contactMethod, setContactMethod] = useState<string>('telegram');
  const [contactValue, setContactValue] = useState<string>('');
  const handleContinue = () => {
    if (!selectedOption) {
      setShowError(true);
      return;
    }
    setStep1Completed(true);
    setStep(2);
    window.scrollTo(0, 0);
  };
  const handleSignUp = () => {
    setStep2Completed(true);
    setStep(3);
    window.scrollTo(0, 0);
  };
  const handleEmailVerified = () => {
    setStep3Completed(true);
    setStep(4);
    window.scrollTo(0, 0);
  };
  const handleSelect = (id: string) => {
    setSelectedOption(id);
    setShowError(false);
  };
  const handleStepClick = (targetStep: 1 | 2 | 3 | 4) => {
    if (targetStep === 1 || targetStep === 2 && step1Completed || targetStep === 3 && step2Completed || targetStep === 4 && step3Completed) {
      setStep(targetStep);
      window.scrollTo(0, 0);
    }
  };
  const toggleTrafficSource = (source: string) => {
    setTrafficSources(prev => prev.includes(source) ? prev.filter(s => s !== source) : [...prev, source]);
  };
  const toggleWayToPromote = (way: string) => {
    setWaysToPromote(prev => prev.includes(way) ? prev.filter(w => w !== way) : [...prev, way]);
  };
  // Step Indicator Component
  const StepIndicator = () => <div className="border-b border-slate-200 bg-white" data-id="element-0">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6" data-id="element-1">
        <div className="flex items-center justify-center gap-2 sm:gap-4 overflow-x-auto" data-id="element-2">
          {/* Step 1 */}
          <button onClick={() => handleStepClick(1)} className={`flex items-center gap-2 sm:gap-3 transition-all flex-shrink-0 ${step === 1 ? 'opacity-100' : step1Completed ? 'opacity-70 hover:opacity-100' : 'opacity-50'}`} disabled={!step1Completed && step !== 1} data-id="element-3">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${step1Completed ? 'bg-indigo-600 border-indigo-600 text-white' : step === 1 ? 'border-indigo-600 text-indigo-600' : 'border-slate-300 text-slate-400'}`} data-id="element-4">
              {step1Completed ? <Check size={16} strokeWidth={3} data-id="element-5" /> : '1'}
            </div>
            <div className="text-left hidden lg:block" data-id="element-6">
              <div className={`text-sm font-medium ${step === 1 ? 'text-slate-900' : 'text-slate-600'}`} data-id="element-7">
                Choose your path
              </div>
              <div className="text-xs text-slate-400" data-id="element-8">Tell us who you are</div>
            </div>
          </button>

          {/* Connector Line */}
          <div className={`w-8 sm:w-12 h-0.5 ${step1Completed ? 'bg-indigo-600' : 'bg-slate-200'}`} data-id="element-9"></div>

          {/* Step 2 */}
          <button onClick={() => handleStepClick(2)} className={`flex items-center gap-2 sm:gap-3 transition-all flex-shrink-0 ${step === 2 ? 'opacity-100' : step1Completed ? 'opacity-70 hover:opacity-100' : 'opacity-50'}`} disabled={!step1Completed} data-id="element-10">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${step2Completed ? 'bg-indigo-600 border-indigo-600 text-white' : step === 2 ? 'border-indigo-600 text-indigo-600' : 'border-slate-300 text-slate-400'}`} data-id="element-11">
              {step2Completed ? <Check size={16} strokeWidth={3} data-id="element-12" /> : '2'}
            </div>
            <div className="text-left hidden lg:block" data-id="element-13">
              <div className={`text-sm font-medium ${step === 2 ? 'text-slate-900' : 'text-slate-600'}`} data-id="element-14">
                Account information
              </div>
              <div className="text-xs text-slate-400" data-id="element-15">Sign up details</div>
            </div>
          </button>

          {/* Connector Line */}
          <div className={`w-8 sm:w-12 h-0.5 ${step2Completed ? 'bg-indigo-600' : 'bg-slate-200'}`} data-id="element-16"></div>

          {/* Step 3 */}
          <button onClick={() => handleStepClick(3)} className={`flex items-center gap-2 sm:gap-3 transition-all flex-shrink-0 ${step === 3 ? 'opacity-100' : step2Completed ? 'opacity-70 hover:opacity-100' : 'opacity-50'}`} disabled={!step2Completed} data-id="element-17">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${step3Completed ? 'bg-indigo-600 border-indigo-600 text-white' : step === 3 ? 'border-indigo-600 text-indigo-600' : 'border-slate-300 text-slate-400'}`} data-id="element-18">
              {step3Completed ? <Check size={16} strokeWidth={3} data-id="element-19" /> : '3'}
            </div>
            <div className="text-left hidden lg:block" data-id="element-20">
              <div className={`text-sm font-medium ${step === 3 ? 'text-slate-900' : 'text-slate-600'}`} data-id="element-21">
                Email verification
              </div>
              <div className="text-xs text-slate-400" data-id="element-22">Verify email</div>
            </div>
          </button>

          {/* Connector Line */}
          <div className={`w-8 sm:w-12 h-0.5 ${step3Completed ? 'bg-indigo-600' : 'bg-slate-200'}`} data-id="element-23"></div>

          {/* Step 4 */}
          <button onClick={() => handleStepClick(4)} className={`flex items-center gap-2 sm:gap-3 transition-all flex-shrink-0 ${step === 4 ? 'opacity-100' : step3Completed ? 'opacity-70 hover:opacity-100' : 'opacity-50'}`} disabled={!step3Completed} data-id="element-24">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${step === 4 ? 'border-indigo-600 text-indigo-600' : step3Completed ? 'border-slate-300 text-slate-600' : 'border-slate-300 text-slate-400'}`} data-id="element-25">
              4
            </div>
            <div className="text-left hidden lg:block" data-id="element-26">
              <div className={`text-sm font-medium ${step === 4 ? 'text-slate-900' : 'text-slate-600'}`} data-id="element-27">
                Account information
              </div>
              <div className="text-xs text-slate-400" data-id="element-28">Awaiting approval</div>
            </div>
          </button>
        </div>
      </div>
    </div>;
  if (step === 4) {
    // STEP 4: Complete your details
    return <div className="min-h-screen bg-white flex flex-col" data-id="element-29">
        {/* Header */}
        <div className="px-8 py-6 flex justify-between items-center border-b border-slate-100" data-id="element-30">
          <div className="flex-shrink-0" data-id="element-31">
            <Icon1 data-id="element-32" />
          </div>
          <div className="text-sm text-slate-600 ml-6" data-id="element-33">
            Have account?{' '}
            <a href="#" className="text-indigo-600 font-medium hover:text-indigo-700" data-id="element-34">
              Sign in
            </a>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator data-id="element-35" />

        {/* Centered Content */}
        <div className="flex-1 flex items-center justify-center px-8 py-12" data-id="element-36">
          <div className="w-full max-w-xl animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-37">
            <div className="bg-white border border-slate-200 rounded-lg p-8" data-id="element-38">
              <div className="mb-6" data-id="element-39">
                <h1 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-40">
                  Complete your details
                </h1>
                <p className="text-slate-600" data-id="element-41">
                  Provide the necessary information to help us process your
                  request
                </p>
              </div>

              {/* Traffic sources */}
              <div className="mb-6" data-id="element-42">
                <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-43">
                  Traffic sources
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-44">
                  {['Own website', 'Own socials (twitter, FB, Tiktok, Insta)', 'Buying traffic from socials', 'Ad network traffic', 'Other'].map(source => <button key={source} onClick={() => toggleTrafficSource(source)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${trafficSources.includes(source) ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:border-indigo-300'}`} data-id="element-45">
                      {source}
                    </button>)}
                </div>
              </div>

              {/* Ways to promote */}
              <div className="mb-6" data-id="element-46">
                <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-47">
                  Ways to promote
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-48">
                  {['Way #1', 'Way #2', 'Way #3'].map(way => <button key={way} onClick={() => toggleWayToPromote(way)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${waysToPromote.includes(way) ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:border-indigo-300'}`} data-id="element-49">
                      {way}
                    </button>)}
                </div>
              </div>

              {/* Do you have your own website or domain? */}
              <div className="mb-6" data-id="element-50">
                <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-51">
                  Do you have your own website or domain?
                </label>
                <div className="flex gap-4" data-id="element-52">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-53">
                    <input type="radio" name="hasWebsite" checked={hasWebsite === 'yes'} onChange={() => setHasWebsite('yes')} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-54" />
                    <span className="text-sm text-slate-700" data-id="element-55">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-56">
                    <input type="radio" name="hasWebsite" checked={hasWebsite === 'no'} onChange={() => setHasWebsite('no')} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-57" />
                    <span className="text-sm text-slate-700" data-id="element-58">No</span>
                  </label>
                </div>
              </div>

              {/* Expected volume of traffic */}
              <div className="mb-6" data-id="element-59">
                <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-60">
                  Expected volume of traffic
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-61">
                  {['0 - 5,000', '5,000 - 50,000', '50,000 - 100,000', '500,000 - 1,000,000', '1,000,000+'].map(volume => <button key={volume} onClick={() => setTrafficVolume(volume)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${trafficVolume === volume ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:border-indigo-300'}`} data-id="element-62">
                      {volume}
                    </button>)}
                </div>
              </div>

              {/* Contact method */}
              <div className="mb-8" data-id="element-63">
                <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-64">
                  Contact method
                </label>
                <div className="flex gap-3" data-id="element-65">
                  <select value={contactMethod} onChange={e => setContactMethod(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-66">
                    <option value="telegram" data-id="element-67">Telegram</option>
                    <option value="whatsapp" data-id="element-68">WhatsApp</option>
                    <option value="email" data-id="element-69">Email</option>
                  </select>
                  <input type="text" placeholder="@example" value={contactValue} onChange={e => setContactValue(e.target.value)} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-70" />
                </div>
              </div>

              {/* Submit Button */}
              <Button className="w-full py-3 shadow-lg shadow-indigo-200" data-id="element-71">
                Submit
              </Button>
            </div>
          </div>
        </div>
      </div>;
  }
  if (step === 3) {
    // STEP 3: Email verification
    return <div className="min-h-screen bg-white flex flex-col" data-id="element-72">
        {/* Header */}
        <div className="px-8 py-6 flex justify-between items-center border-b border-slate-100" data-id="element-73">
          <div className="flex-shrink-0" data-id="element-74">
            <Icon1 data-id="element-75" />
          </div>
          <div className="text-sm text-slate-600 ml-6" data-id="element-76">
            Have account?{' '}
            <a href="#" className="text-indigo-600 font-medium hover:text-indigo-700" data-id="element-77">
              Sign in
            </a>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator data-id="element-78" />

        {/* Centered Content */}
        <div className="flex-1 flex items-center justify-center px-8 py-12" data-id="element-79">
          <div className="w-full max-w-lg animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-80">
            {/* Email Icon */}
            <div className="flex justify-center mb-8" data-id="element-81">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-82">
                <Mail size={40} className="text-slate-600" data-id="element-83" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6" data-id="element-84">
              <h1 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-85">
                Your account is created - verify your email
              </h1>
              <p className="text-slate-600" data-id="element-86">
                We've sent a confirmation email to{' '}
                <span className="font-semibold text-slate-900" data-id="element-87">
                  {userEmail}
                </span>{' '}
                <button className="text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-88">
                  (Change email)
                </button>
              </p>
              <p className="text-slate-600 mt-2" data-id="element-89">
                Please click the link in the email to verify your address and
                continue.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-90">
              <Button onClick={handleEmailVerified} className="px-8 py-3 shadow-lg shadow-indigo-200" data-id="element-91">
                Open inbox
              </Button>
              <button className="px-8 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors" data-id="element-92">
                Resend link
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  if (step === 2) {
    // STEP 2: Full-width centered registration form
    return <div className="min-h-screen bg-white flex flex-col" data-id="element-93">
        {/* Header */}
        <div className="px-8 py-6 flex justify-between items-center border-b border-slate-100" data-id="element-94">
          <div className="flex-shrink-0" data-id="element-95">
            <Icon1 data-id="element-96" />
          </div>
          <div className="text-sm text-slate-600 ml-6" data-id="element-97">
            Have account?{' '}
            <a href="#" className="text-indigo-600 font-medium hover:text-indigo-700" data-id="element-98">
              Sign in
            </a>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator data-id="element-99" />

        {/* Centered Content */}
        <div className="flex-1 flex items-center justify-center px-8 py-12" data-id="element-100">
          <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-101">
            <div className="mb-8 text-center" data-id="element-102">
              <h1 className="text-3xl font-bold text-slate-900" data-id="element-103">
                Create an account
              </h1>
            </div>

            <button className="w-full flex items-center justify-center gap-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium py-3 px-4 rounded-lg transition-colors mb-8" data-id="element-104">
              <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-105">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-106" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-107" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-108" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-109" />
              </svg>
              Sign up with Google
            </button>

            <div className="relative mb-8" data-id="element-110">
              <div className="absolute inset-0 flex items-center" data-id="element-111">
                <div className="w-full border-t border-slate-200" data-id="element-112"></div>
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-113">
                <span className="px-4 bg-white text-slate-500" data-id="element-114">
                  or fill details manually
                </span>
              </div>
            </div>

            <div className="space-y-5 mb-8" data-id="element-115">
              <Input label="Username" placeholder="Enter your username" data-id="element-116" />
              <Input label="Email" type="email" placeholder="email@example.com" value={userEmail} onChange={e => setUserEmail(e.target.value)} data-id="element-117" />
              <Input label="Password" type="password" placeholder="Create a password" data-id="element-118" />
              <Input label="Repeat password" type="password" placeholder="Repeat your password" data-id="element-119" />
            </div>

            <div className="flex items-center gap-3 mb-8" data-id="element-120">
              <div className="flex items-center h-5" data-id="element-121">
                <input id="terms" type="checkbox" className="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-122" />
              </div>
              <label htmlFor="terms" className="text-sm text-slate-700" data-id="element-123">
                I agree with{' '}
                <a href="#" className="text-indigo-600 hover:text-indigo-700" data-id="element-124">
                  Terms and conditions
                </a>
              </label>
            </div>

            {/* Fake reCAPTCHA */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between mb-8 w-full max-w-[300px]" data-id="element-125">
              <div className="flex items-center gap-3" data-id="element-126">
                <div className="w-6 h-6 border-2 border-slate-300 rounded bg-white" data-id="element-127"></div>
                <span className="text-sm text-slate-700 font-medium" data-id="element-128">
                  I'm not a robot
                </span>
              </div>
              <div className="flex flex-col items-center text-[10px] text-slate-400" data-id="element-129">
                <RefreshCw size={20} className="mb-1 text-blue-500" data-id="element-130" />
                <span data-id="element-131">reCAPTCHA</span>
                <span data-id="element-132">Privacy - Terms</span>
              </div>
            </div>

            <Button onClick={handleSignUp} className="w-full py-3 shadow-lg shadow-indigo-200" data-id="element-133">
              Sign up
            </Button>
          </div>
        </div>
      </div>;
  }
  // STEP 1: Split layout with illustration
  return <div className="min-h-screen flex flex-col lg:flex-row bg-white" data-id="element-134">
      {/* Left Column */}
      <div className="w-full lg:w-1/2 flex flex-col relative min-h-screen lg:min-h-0" data-id="element-135">
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100" data-id="element-136">
          <Icon1 data-id="element-137" />
        </div>

        {/* Step Indicator */}
        <StepIndicator data-id="element-138" />

        {/* Content Container - Vertically Centered */}
        <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12" data-id="element-139">
          <div className="max-w-lg w-full mx-auto lg:mx-0" data-id="element-140">
            <div className="animate-in fade-in slide-in-from-left-4 duration-300" data-id="element-141">
              <div className="mb-10" data-id="element-142">
                <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3" data-id="element-143">
                  Let's get your traffic earning
                </h1>
                <p className="text-lg text-slate-600" data-id="element-144">
                  Tell us who you are so we can set things up correctly.
                </p>
              </div>

              <div className="space-y-4 mb-8" data-id="element-145">
                <OptionCard id="traffic-owner" title="I have traffic" description="I monetize visitors from my own resources like websites, SEO, tubes, and communities." isSelected={selectedOption === 'traffic-owner'} onClick={() => handleSelect('traffic-owner')} data-id="element-146" />

                <OptionCard id="media-buyer" title="I buy traffic" description="I focus on media buying, arbitrage, and optimizing funnels to scale ROI." isSelected={selectedOption === 'media-buyer'} onClick={() => handleSelect('media-buyer')} data-id="element-147" />
              </div>

              <div className="mb-10" data-id="element-148">
                {showError && <div className="flex items-center gap-2 text-red-600 mb-3 animate-in fade-in slide-in-from-bottom-1" data-id="element-149">
                    <AlertCircle size={16} data-id="element-150" />
                    <span className="text-sm font-medium" data-id="element-151">
                      Please select an option to continue
                    </span>
                  </div>}

                <Button onClick={handleContinue} className="w-full md:w-auto md:min-w-[300px] shadow-lg shadow-indigo-200" data-id="element-152">
                  Get started
                </Button>
                <p className="text-sm text-slate-400 mt-3" data-id="element-153">
                  Takes less than 2 minutes • You can update this later
                </p>
              </div>

              <div className="border-t border-slate-200 pt-6" data-id="element-154">
                <div className="flex flex-col gap-4 text-sm text-slate-500" data-id="element-155">
                  <a href="#" className="flex items-start gap-3 hover:text-slate-700 transition-colors group" data-id="element-156">
                    <Users size={18} className="text-slate-400 group-hover:text-slate-600 mt-0.5 shrink-0" data-id="element-157" />
                    <span className="leading-tight" data-id="element-158">
                      Want to earn on referrals? Invite affiliates or models.
                    </span>
                  </a>
                  <a href="#" className="flex items-start gap-3 hover:text-slate-700 transition-colors group" data-id="element-159">
                    <Video size={18} className="text-slate-400 group-hover:text-slate-600 mt-0.5 shrink-0" data-id="element-160" />
                    <span className="leading-tight" data-id="element-161">
                      Model or studio? Go to Stripchat
                    </span>
                    <ArrowRight size={16} className="text-slate-400 group-hover:text-slate-600 transition-transform group-hover:translate-x-0.5 mt-0.5 shrink-0" data-id="element-162" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Full Height Grey Background */}
      <div className="hidden lg:flex lg:w-1/2 bg-gray-100 items-center justify-center p-12 min-h-screen sticky top-0 h-screen" data-id="element-163">
        <div className="w-full max-w-2xl" data-id="element-164">
          <img src="https://cdn.magicpatterns.com/uploads/1qHgYApZ5rdyM3TapYKCog/traffic-analysis.DEFdIqmz.webp" alt="Traffic analysis dashboard showing impressions and clicks" className="w-full h-auto object-contain" data-id="element-165" />
        </div>
      </div>
    </div>;
}